<template>
  <div class="kefu-fullscreen">
    <!-- 全屏客服iframe -->
    <div class="fullscreen-container">
      <!-- 加载状态 -->
      <div class="loading-container" v-if="isLoading">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>

      <!-- 客服iframe -->
      <iframe
        :src="iframeUrl"
        class="kefu-iframe"
        :class="{ 'iframe-hidden': isLoading }"
        frameborder="0"
        allowfullscreen
        @load="onIframeLoad">
      </iframe>
    </div>
  </div>
</template>

<script>
import { getUser, getUserId, getRemark } from '@/utils/auth'

export default {
  name: 'KefuFullscreen',
  data() {
    return {
      isLoading: true,
      fallbackTimer: null,
      meiqiaReady: false,
      conversationActive: false,
      kefuLink: ''
    }
  },
  computed: {
    iframeUrl() {
      if (!this.kefuLink) return ''

      // 如果是qiabot链接，注入顾客信息后返回
      if (this.kefuLink.includes('qiabot.html')) {
        return this.enhanceQiabotUrl(this.kefuLink)
      }

      // 如果链接已经包含chatlink.html，直接使用
      if (this.kefuLink.includes('chatlink.html')) {
        return this.enhanceChatLinkUrl(this.kefuLink)
      }

      // 否则构建新的chatlink.html URL
      return this.buildMeiqiaIframeUrl(this.kefuLink)
    },
    loadingText() {
      if (!this.meiqiaReady) {
        return this.$t('kefu.initializing')
      } else if (this.meiqiaReady && !this.conversationActive) {
        return this.$t('kefu.connecting')
      } else {
        return this.$t('kefu.loading')
      }
    }
  },
  mounted() {
    // 获取传递的客服链接
    this.kefuLink = this.$route.query.link || ''
    
    if (!this.kefuLink) {
      this.$toast(this.$t('kefu.linkInvalid'))
      this.$router.go(-1)
      return
    }

    // 添加消息监听器
    this.addMessageListener()
    // 设置备用超时机制
    this.setFallbackTimer()

    // 隐藏状态栏（如果在APP中）
    this.hideStatusBar()
  },
  beforeDestroy() {
    // 组件销毁前移除消息监听器
    this.removeMessageListener()
    // 清除备用定时器
    this.clearFallbackTimer()
    // 恢复状态栏
    this.showStatusBar()
  },
  methods: {
    // 构建美洽iframe URL
    buildMeiqiaIframeUrl(link) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''
      
      // 解析原始链接中的entId
      let entId = ''
      let match = link.match(/eid=([^&]+)/)
      if (match) {
        entId = match[1]
      } else {
        match = link.match(/entId=([^&]+)/)
        if (match) {
          entId = match[1]
        }
      }
      
      // 构建chatlink.html的URL
      const baseUrl = window.location.origin + '/chatlink.html'
      const params = new URLSearchParams({
        entId: entId,
        ID: userId,
        name: userName,
        username: userName,
        remark: remark,
        language: 'en'
      })
      
      return `${baseUrl}?${params.toString()}`
    },
    // 增强 qiabot.html 链接，注入顾客身份与信息
    enhanceQiabotUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      const urlObj = new URL(url, window.location.origin)

      if (userId) {
        urlObj.searchParams.set('clientid', userId)
      }

      const metadata = {}
      if (userName) metadata.name = userName
      if (remark) metadata.comment = remark
      if (Object.keys(metadata).length > 0) {
        urlObj.searchParams.set('metadata', JSON.stringify(metadata))
      }

      return urlObj.toString()
    },
    // 增强已有的chatlink.html链接，添加用户信息
    enhanceChatLinkUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      // 解析现有URL
      const urlObj = new URL(url, window.location.origin)

      // 添加或更新用户信息参数
      if (userId) urlObj.searchParams.set('ID', userId)
      if (userName) {
        urlObj.searchParams.set('name', userName)
        urlObj.searchParams.set('username', userName)
      }
      if (remark) urlObj.searchParams.set('remark', remark)

      // 确保有语言参数
      if (!urlObj.searchParams.has('language')) {
        urlObj.searchParams.set('language', 'en')
      }

      return urlObj.toString()
    },
    // 处理美洽自带的返回按钮事件
    handleMeiqiaClose() {
      console.log('美洽客服关闭，返回上一页')
      this.$router.go(-1)
    },
    onIframeLoad() {
      console.log('📄 iframe DOM加载完成，等待美洽初始化...')
      
      // 如果iframe加载完成但美洽还没准备好，给一个较短的等待时间
      setTimeout(() => {
        if (this.isLoading && !this.meiqiaReady) {
          console.log('⏰ iframe加载完成但美洽未就绪，继续等待美洽回调...')
        }
      }, 2000)
    },
    // 添加消息监听器
    addMessageListener() {
      window.addEventListener('message', this.handleMessage)
    },
    // 移除消息监听器
    removeMessageListener() {
      window.removeEventListener('message', this.handleMessage)
    },
    // 处理来自iframe的消息
    handleMessage(event) {
      if (!event.data || !event.data.type) return

      console.log('🔔 收到美洽postMessage:', event.data)

      switch (event.data.type) {
        case 'meiqia_ready':
          console.log('✅ 美洽客服初始化完成')
          console.log('📊 消息来源:', event.data.source || 'callback')
          console.log('📊 客服在线状态:', event.data.servability)
          
          // 标记美洽已就绪
          this.meiqiaReady = true
          
          // 美洽初始化完成，隐藏加载状态
          this.isLoading = false
          // 清除备用定时器
          this.clearFallbackTimer()
          break

        case 'meiqia_conversation_start':
          console.log('✅ 美洽会话开始')
          this.conversationActive = true
          break

        case 'meiqia_conversation_end':
          console.log('✅ 美洽会话结束')
          this.conversationActive = false
          break

        case 'meiqia_before_close':
          console.log('✅ 美洽准备关闭')
          this.conversationActive = false
          // 美洽自带返回按钮被点击，返回上一页
          this.handleMeiqiaClose()
          break

        default:
          console.log('🔍 其他美洽消息类型:', event.data.type)
      }
    },
    // 设置备用超时机制
    setFallbackTimer() {
      this.clearFallbackTimer()
      this.fallbackTimer = setTimeout(() => {
        if (this.isLoading && !this.meiqiaReady) {
          console.log('⚠️ 美洽回调超时，使用备用方案隐藏加载状态')
          this.isLoading = false
        }
      }, 15000) // 15秒超时
    },
    // 清除备用定时器
    clearFallbackTimer() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer)
        this.fallbackTimer = null
      }
    },
    // 隐藏状态栏（APP环境）
    hideStatusBar() {
      try {
        if (window.StatusBar && window.StatusBar.hide) {
          window.StatusBar.hide()
        }
      } catch (e) {
        console.log(this.$t('kefu.statusBarUnavailable'))
      }
    },
    // 显示状态栏（APP环境）
    showStatusBar() {
      try {
        if (window.StatusBar && window.StatusBar.show) {
          window.StatusBar.show()
        }
      } catch (e) {
        console.log(this.$t('kefu.statusBarUnavailable'))
      }
    }
  }
}
</script>

<style lang="less" scoped>
.kefu-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  z-index: 10000;
}

.fullscreen-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 5;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(24, 144, 255, 0.3);
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.kefu-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
  transition: opacity 0.3s ease;

  &.iframe-hidden {
    opacity: 0;
  }
}

/* 安全区域适配 */
@supports (padding-top: env(safe-area-inset-top)) {
  .kefu-fullscreen {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
